import { ApplicationController, randomId } from '@aha-app/mvc';
import TokenManager, {
  type TokenPayload,
} from 'components/aha_editor_field_v2/collaboration/TokenManager';
import ReliableCollaborationServer from 'components/aha_editor_field_v2/collaboration/ReliableCollaborationServer';
import ShareDB from 'sharedb/lib/client';
import { createFayeClient } from 'javascripts/faye_client';
import AhaDrawer from 'javascripts/drawer';
import AhaBottomDrawer from 'javascripts/bottom_drawer';
import * as Sentry from '@sentry/browser';
import { CardLayout, Page, Whiteboard } from 'components/framework/models';
import {
  getQueryParam,
  replaceQueryParam,
} from 'components/common/useQueryParam';
import {
  type RecordId,
  WHITEBOARD_RECORD_TYPES,
  type WhiteboardCardLayoutEditorFunction,
  type RecordReferenceStruct,
  type WhiteboardDisplayMode,
  isWhiteboardDisplayMode,
  type SidePanelItem,
  type WhiteboardLoadDefaultCardLayoutFunction,
} from 'javascripts/shared-types';
import { sharedRecordPickerController } from 'components/record_picker_modal';
import { sharedCardLayoutController } from 'components/CardLayoutEditor/controllers/CardLayoutEditorController';
import { pathForRecordRef } from 'javascripts/url_helpers';
import Router, { makeUrl } from './Router';
import Debug from 'debug';
import { assistantSidePanelConfiguration } from '../Assistant';
import {
  type MeasureText,
  type DiagramNode,
  generateDiagram,
  type Diagram,
} from '@aha-app/diagrams';
import {
  type WhiteboardController,
  type FayeClient,
} from '@aha-app/whiteboard';
import { type RecordPickerOptions } from 'components/record_picker_modal/controllers/RecordPickerController';
import type AssistantViewController from 'components/Assistant/components/AssistantView/AssistantViewController';
import AssistedWhiteboard from 'components/Assistant/agents/WhiteboardAgent/AssistedWhiteboard';

const TOKEN_REFRESH_INTERVAL = 4 * 60 * 1000;

const debug = Debug('whiteboard:ui');

type DiagramResponse = { diagram: string; truncated?: boolean; limit: number };

export type DisplayMode = {
  mode: WhiteboardDisplayMode;
  previousMode?: WhiteboardDisplayMode;

  // used to focus a comment when transitioning to full screen. it's here because we
  // don't want it to persist beyond the one fullscreen transition - if the user exits
  // then goes to fullscreen again, it should not focus the comment again
  focusedAnnotationId?: RecordId;

  // tracks whether the transition to this mode has been actioned, so we can avoid calling it multiple times
  transitioned?: boolean;

  // whether to animate the transition
  animate: boolean;
};

interface SharedWhiteboard {
  id: RecordId;
  created_at?: string;
  editor_content: Record<string, any>;
  collaborative_version: number;
  selection_token: string;
}

interface AttachmentType {
  id: RecordId;
  download_url: string;
}

interface State {
  /** The editing mode allowed. */
  editable: Editable;
  /** Whether full screen is currently allowed. */
  allowFullScreen: boolean;
  /** Whether we should show the container header */
  showContainerHeader: boolean;

  /* controls how the whiteboard is presented */
  displayMode: DisplayMode;
  /** Account ID of whiteboard record to display. */
  accountId: string | null | undefined;
  /** ID of whiteboard record to display. */
  whiteboardId: string | null;
  /** URL of the whiteboard for making object links */
  whiteboardUrl?: string | null;
  /** Whether the token is ready. */
  tokenReady: boolean;
  /** Whether the token allows us to update. */
  allowUpdate: boolean;
  /** Whether the token allows us to annotate. */
  allowAnnotate: boolean;

  /** Instance of a whiteboard. */
  instanceId: string | null;

  /** Details required for accessing shared whiteboards. */
  sharingOptions: SharingOptions;

  commentsVisible: boolean;

  /** The project */
  defaultProject?: { id: RecordId; name: string };

  focusedAnnotationId: string | null;
  whiteboard?: Whiteboard | null;
  modalMessageTitle: string | null;
  modalMessageProgress: number;
  commentsEnabled?: boolean;
  unresolvedCommentsCount: number | null;

  // if whiteboard.page.editLocked is true, this controls whether the whiteboard is editable
  editingLockedDocument: boolean;

  whiteboardReady: boolean;
  tokenFailure: boolean;
}

export interface WhiteboardContainerControllerProps {
  whiteboardId: RecordId;
  whiteboardUrl?: string;
  editable?: Editable;
  allowFullScreen?: boolean;
  showContainerHeader: boolean;
  sharingOptions?: SharingOptions;
  defaultProject?: { id: RecordId; name: string };
  editor?: any;
  node?: any;
  commentsEnabled?: boolean;
  attachmentUrls?: Record<RecordId, string>;
  sidepanelDisabled?: boolean;
  collaborative?: boolean;
  containingPageId?: RecordId;
}

interface SharingOptions {
  collaborationBaseURL?: string;
  collaborationServerURL?: string;
  connectedAccountId?: string;
  csrfToken?: string;
  getSharingToken?: () => string;
}

interface WhiteboardContainerController {
  shareDBConnection: ShareDB.Connection;
  whiteboardController: WhiteboardController;
}

type RouteInfo = {
  mode: WhiteboardDisplayMode;
  annotationId?: RecordId;
};

// Get annotation ID from the hash, checking the drawer URL if it's in the drawer
const getFocusedAnnotationId = (whiteboardId: string) => {
  const drawer = AhaDrawer.instance();
  const drawerUrl = drawer?.currentUrl();
  const inDrawer =
    drawerUrl && drawer?.$el().find(`[data-whiteboard-id="${whiteboardId}"]`);
  const location = inDrawer
    ? new URL(drawerUrl, window.location.origin)
    : window.location;
  const hash = decodeURIComponent(location.hash.slice(1));

  if (hash.startsWith('annotation_')) {
    return hash.replace('annotation_', '') as RecordId;
  }
};

const parseRouteInfo = (
  whiteboardId: string,
  params: URLSearchParams
): RouteInfo => {
  const annotationId = getFocusedAnnotationId(whiteboardId);
  const whiteboard = params.get('whiteboard');
  if (whiteboard) {
    const [id, mode] = whiteboard.split('--');
    if (id === whiteboardId && isWhiteboardDisplayMode(mode)) {
      return {
        mode,
        annotationId,
      };
    } else {
      console.warn('Invalid whiteboard parameter:', whiteboard);
    }
  }

  return { mode: 'inline', annotationId };
};

class WhiteboardContainerController extends ApplicationController<
  State,
  WhiteboardContainerControllerProps
> {
  // Whiteboard is immediately editable.
  static EDITING_EDITABLE: Editable = 'editable';
  // Whiteboard is always readonly.
  static EDITING_READ_ONLY: Editable = 'readOnly';
  // Whiteboard is only editable when in full screen.
  static EDITING_EDITABLE_IN_FULL_SCREEN: Editable = 'fullScreenEditable';

  static initialState: State = {
    // The editing mode allowed.
    editable: WhiteboardContainerController.EDITING_EDITABLE,
    // Whether full screen is currently allowed.
    allowFullScreen: true,
    // Whether we should show the container header
    showContainerHeader: false,
    displayMode: { mode: 'inline', animate: false },
    // Account ID of whiteboard record to display.
    accountId: null,
    // ID of whiteboard record to display.
    whiteboardId: null,
    // Canonical URL of the record the whiteboard is on
    whiteboardUrl: null,
    // Whether the token is ready.
    tokenReady: false,
    // Whether the token allows us to update.
    allowUpdate: false,
    // Whether the token allows us to annotate.
    allowAnnotate: false,

    // Instance of a whiteboard.
    instanceId: null,

    // Details required for accessing shared whiteboards
    sharingOptions: {},

    commentsVisible: true,

    // If an annotation id hash is present, focus the comment on the board
    focusedAnnotationId: null,
    whiteboard: null,

    modalMessageTitle: null,
    modalMessageProgress: 0,
    unresolvedCommentsCount: null,

    editingLockedDocument: false,
    whiteboardReady: false,
    tokenFailure: false,
  };

  tokenManager = null as TokenManager | null;
  websocketServer = null as ReliableCollaborationServer | null;
  fayeClient: FayeClient | null = null;

  attachmentUrls: Record<RecordId, string> = {};

  collaborative: boolean;

  inDocNode?: HTMLDivElement | null;

  assistantSidePanel: SidePanelItem | null;

  public router: Router<RouteInfo>;

  async initialize({
    whiteboardId,
    whiteboardUrl,
    editable,
    allowFullScreen,
    showContainerHeader,
    sharingOptions = {},
    defaultProject,
    commentsEnabled,
    attachmentUrls = {},
  }: WhiteboardContainerControllerProps) {
    const shared =
      sharingOptions.collaborationBaseURL &&
      sharingOptions.collaborationBaseURL !== window.location.origin;

    this.router = new Router(
      (_, params) => parseRouteInfo(whiteboardId, params),
      ({ mode, annotationId }, userInitiated) => {
        this.setDisplayMode(
          mode,
          annotationId,
          userInitiated // don't animate on initial load or popstate
        );
      },
      {
        enabled: () =>
          !shared &&
          (!this.whiteboardIsInDrawer ||
            window.featureFlags?.awt_150_drawer_routing),
      }
    );

    this.state.instanceId = Math.random().toString(16).substr(2, 8);
    this.state.whiteboardId = whiteboardId;
    this.state.editable = editable || this.state.editable;
    this.state.whiteboardUrl = whiteboardUrl;
    this.state.defaultProject = defaultProject;
    this.state.commentsEnabled = commentsEnabled;

    this.attachmentUrls = attachmentUrls;
    this.collaborative = this.props.collaborative ?? !window.isPublishing;

    if (allowFullScreen !== undefined)
      this.state.allowFullScreen = allowFullScreen;
    if (showContainerHeader !== undefined)
      this.state.showContainerHeader = showContainerHeader;

    if (sharingOptions.collaborationBaseURL === undefined)
      sharingOptions.collaborationBaseURL = window.location.origin;

    this.state.accountId =
      sharingOptions.connectedAccountId || window.currentAccount?.id;

    this.state.sharingOptions = sharingOptions;
    if (this.collaborative) {
      await this.prepareFayeConnection(sharingOptions);
      this.prepareShareDBConnection(sharingOptions);
    }

    document.addEventListener(
      'aha.whiteboard.export',
      this.exportWhiteboardEventListener
    );

    if (this.collaborative && !shared && this.state.whiteboardId) {
      this.state.whiteboard = await Whiteboard.select('id', 'usedTemplateIds')
        .merge({
          page: Page.select(['id', 'name', 'editLocked', 'path']).merge({
            user: ['id'], // Used for page.isPersonal
            project: ['id'], // Used for page.isPersonal
          }),
        })
        .find(this.state.whiteboardId);
    }

    if (this.state.whiteboard) {
      // Need to listen for updates to usedTemplateIds for the case of adding the
      // first template (causes the Best Practices panel to be added to the sidebar)
      this.state.whiteboard.reactive({});
    }

    this.importFromBookmarkInQueryParams().catch(error => {
      console.error('Error importing from bookmark:', error);
    });

    this.importFromVisualizeProjectInQueryParams().catch(error => {
      console.error('Error importing from project:', error);
    });

    void this.fireWhenWhiteboardIsReady(() => {
      this.state.whiteboardReady = true;
    });

    if (window.featureFlags?.awt_390_whiteboard_ai_assistant) {
      let assistedWhiteboard: AssistedWhiteboard;
      this.assistantSidePanel = assistantSidePanelConfiguration({
        agentName: 'WhiteboardAgent',
        agentParams: {
          projectId: this.state.defaultProject?.id,
          whiteboardId,
        },
        agentInstances: {
          getWhiteboardController: () => this.whiteboardController,
          initializeWhiteboardAgent: (controller: AssistantViewController) => {
            void this.fireWhenWhiteboardIsReady(() => {
              if (!assistedWhiteboard) {
                assistedWhiteboard = new AssistedWhiteboard(
                  this.whiteboardController
                );
                assistedWhiteboard.registerSelectionChangeListener();
                assistedWhiteboard.attachToAssistantViewController(controller);
              }
            });
          },
        },
        alwaysRendered: true,
      });
    }
  }

  async actionUpdateUnresolvedCommentCount() {
    const { commentsEnabled, whiteboardId } = this.state;
    if (
      commentsEnabled &&
      whiteboardId &&
      !this.fromOtherAccount() &&
      !this.isShared
    ) {
      const whiteboard = await Whiteboard.select(
        'id',
        'unresolvedCommentsCount'
      ).find(whiteboardId);

      if (whiteboard) {
        this.state.unresolvedCommentsCount = whiteboard.unresolvedCommentsCount;
      }
    }
  }

  changeProps({ editable }: WhiteboardContainerControllerProps) {
    this.state.editable = editable || this.state.editable;
  }

  destroy() {
    if (this.websocketServer) {
      this.websocketServer.disconnect();
    }
    // We don't need to disconnect from the faye server, because the
    // connection is reused when the endpoint URL is the same.

    document.removeEventListener(
      'aha.whiteboard.export',
      this.exportWhiteboardEventListener
    );

    if (this.state.whiteboard) {
      this.state.whiteboard.unreactive();

      if (this.state.whiteboard.page) {
        this.state.whiteboard.page.unreactive();
      }
    }

    this.inDocNode = undefined;
    this.router.destroy();
  }

  exportWhiteboardEventListener = event => {
    if (!this.whiteboardController?.actionExportWhiteboard) {
      return;
    }
    this.whiteboardController.actionExportWhiteboard(event.detail.type);
  };

  documentCollection() {
    return `${this.state.accountId}--whiteboard`;
  }

  documentId() {
    if (!this.websocketServer) return undefined;

    return `${this.state.whiteboardId}--${this.websocketServer.clientId}--${this.state.instanceId}`;
  }

  fromOtherAccount() {
    if (!window.currentAccount) {
      return true;
    }
    return this.state.accountId !== window.currentAccount.id;
  }

  isEditLocked() {
    return (
      this.state.whiteboard?.page?.editLocked &&
      !this.state.editingLockedDocument
    );
  }

  // We connect to the shareDB server with a dance:
  // 1. First connect the shareDB connection.
  // 2. Then attach the token manager, get a token over the API and send to faye over websocket.
  // 3. Then subscribe to the shareDB document - we need to wait for the token
  //    to be ready before we do this.
  prepareShareDBConnection(sharingOptions: SharingOptions) {
    // Collaborative notes use a single sharedb connection across all notes.
    // We can't share that connection because it is inside @aha-app/aha-editor.
    // So we create our own websocket here. This could be improved by sharing
    // a single websocket across all whiteboards.
    this.websocketServer = new ReliableCollaborationServer({
      baseURL: sharingOptions.collaborationBaseURL,
      // @ts-ignore ReliableCollaborationServer is not typed.
      collaborationURL: sharingOptions.collaborationServerURL,
    });
    this.websocketServer.connect();

    this.shareDBConnection = new ShareDB.Connection(
      // @ts-expect-error socket is created by the connect call above, so we know it's there
      this.websocketServer.socket
    );
    if (this.collaborative) {
      this.prepareTokenManager(sharingOptions);
    }
  }

  prepareTokenManager(sharingOptions: SharingOptions) {
    const tokenDidChange = (newTokenData: TokenPayload) => {
      if (newTokenData) {
        // this.setState({ isAuthenticated: true });
        this.state.allowUpdate = newTokenData.allow_update;
        this.state.allowAnnotate = newTokenData.allow_annotate;
        // this.ahaEditor.collaborativeDocument.rollback();
      } else {
        // Authenticated.
      }
    };

    this.tokenManager = new TokenManager(
      this.websocketServer,
      `${sharingOptions.collaborationBaseURL}/whiteboards/${this.state.whiteboardId}`,
      this.documentCollection(),
      this.documentId() as string,
      TOKEN_REFRESH_INTERVAL,
      tokenDidChange,
      sharingOptions.getSharingToken,
      () => {
        this.state.tokenFailure = true;
      }
    );

    this.tokenManager.connect(() => {
      this.state.tokenReady = true;
    });
  }

  get authorizationHeaders(): Record<string, string> {
    const { sharingOptions } = this.state;
    const authorization =
      sharingOptions.getSharingToken && sharingOptions.getSharingToken();
    return authorization ? { Authorization: `Bearer ${authorization}` } : {};
  }

  get isShared() {
    return (
      this.state.sharingOptions.collaborationBaseURL !== window.location.origin
    );
  }

  async prepareFayeConnection(sharingOptions) {
    let selectionToken = null as string | null;

    if (this.fromOtherAccount()) {
      // We need to get a faye token, because we can't use the account-wide one.
      const response = await fetch(
        `${sharingOptions.collaborationBaseURL}/whiteboards/${this.state.whiteboardId}.json`,
        { headers: this.authorizationHeaders }
      )
        .then(
          response =>
            response.json() as Promise<{ whiteboard: SharedWhiteboard }>
        )
        .catch(error => {
          Sentry.captureException(error);
          console.error('Failed to fetch token:', error);
        });

      if (response) {
        selectionToken = response.whiteboard.selection_token;
      }
    }

    const options = sharingOptions.selectionURL && {
      url: sharingOptions.selectionURL,
      tokens: {
        collaborativeSelection: selectionToken,
      },
    };

    this.fayeClient = createFayeClient(options);
  }

  // If a bookmark id is passed in the query parameters, then we import the
  // data for that bookmark into the whiteboard.
  private async importFromBookmarkInQueryParams() {
    const bookmarkId = getQueryParam('bookmark_id');
    const exportType = getQueryParam('export_type');

    if (bookmarkId) {
      replaceQueryParam('bookmark_id', null);
      replaceQueryParam('export_type', null);
      if (exportType === 'records') {
        await this.actionAddRecordsFromBookmark(bookmarkId);
      } else {
        await this.actionAddViewFromBookmark(bookmarkId);
      }
    }
  }

  private async actionAddRecordsFromBookmark(bookmarkId) {
    debug(`Adding records from bookmark ${bookmarkId}`);

    this.state.modalMessageTitle = 'Export report to whiteboard';
    this.state.modalMessageProgress = 30;
    const response = await fetch(`/bookmarks/${bookmarkId}/whiteboards.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': window.csrfToken(),
      },
    });
    this.state.modalMessageProgress = 80;

    const json = await response.json();

    await this.fireWhenWhiteboardIsReady(() => {
      this.whiteboardController.actionPlaceObjects(json as any);
    });

    this.state.modalMessageTitle = null;
    this.state.modalMessageProgress = 0;
  }

  private async importFromVisualizeProjectInQueryParams() {
    const projectId = getQueryParam('visualize_project_id');

    if (projectId) {
      replaceQueryParam('visualize_project_id', null);
      await this.actionAddRecordsFromProject(projectId);
    }
  }

  private async actionAddRecordsFromProject(projectId: string) {
    debug(`Adding records from project ${projectId}`);

    this.state.modalMessageTitle =
      'Visualize workspace hierarchy in whiteboard';
    this.state.modalMessageProgress = 30;
    let url = '/api/v1/products/picker_all.diagram';
    if (projectId !== 'all') {
      url += '?project_id=' + projectId;
    }
    const response = await fetch(url);
    this.state.modalMessageProgress = 80;

    const body = (await response.json()) as DiagramResponse;

    if (response.status > 299) {
      console.error('Invalid response from server');
      let message = 'There was an error generating the diagram';
      if (body && typeof body === 'object' && 'error' in body) {
        message += `. Error: ${body.error}`;
      }
      this.fireWhenWhiteboardIsReady(() => {
        this.whiteboardController.actionShowMessage(
          {
            message,
            type: 'error',
          },
          false,
          false,
          6000
        );
      });
      this.state.modalMessageTitle = null;
      this.state.modalMessageProgress = 0;
      return;
    }
    const diagram = generateDiagram(body.diagram, {
      widthHint: 2000,
      heightHint: 1000,
    });
    const json = await renderDiagramToProjectRecords(diagram);

    await this.fireWhenWhiteboardIsReady(() => {
      this.whiteboardController.actionPlaceObjects(json);
      if (body.truncated) {
        // Add notice saying some items are missing
        const noticeId = randomId();
        const notice = {
          objects: {
            [noticeId]: {
              type: 'shape',
              shapeName: 'roundRect',
              id: noticeId,
              textContent: `<p style="color:#958D6C">The export exceeded the maximum size allowed, so some workspaces were not included.\nGo to <a href="/settings/account">account settings</a> to export additional workspaces or workspace lines.</p>`,
              left: 0,
              top: 0,
              width: 700,
              height: 80,
              scaleX: 1,
              scaleY: 1,
              fill: '#FFF9E3',
              stroke: '#F9E7A6',
              strokeWidth: 1,
              opacity: 1,
            },
          },
        };
        this.whiteboardController.actionPlaceObjects(notice, {
          placement: 'bottom',
        });
      }
    });

    this.state.modalMessageTitle = null;
    this.state.modalMessageProgress = 0;
  }

  private async actionAddViewFromBookmark(bookmarkId) {
    await this.fireWhenWhiteboardIsReady(() =>
      this.whiteboardController.actionPlaceEmbeddedBookmark(bookmarkId)
    );
  }

  async fireWhenWhiteboardIsReady(action: () => void) {
    return new Promise(resolve => {
      const func = () => {
        if (this.whiteboardController?.state?.isLoaded) {
          resolve(action());
        } else {
          setTimeout(func, 100);
        }
      };

      func();
    });
  }

  actionSetWhiteboardController(wbController: ApplicationController) {
    if (!this.whiteboardController) {
      const controller = wbController.findController(controller =>
        controller.constructor.name.includes('WhiteboardController')
      );
      if (controller) {
        this.whiteboardController = controller as WhiteboardController;
      }
    }
  }

  public getDisplayModeUrl(displayMode: WhiteboardDisplayMode, hash = '') {
    const params = {
      whiteboard:
        displayMode === 'inline'
          ? undefined
          : `${this.props.whiteboardId}--${displayMode}`,
    };
    return makeUrl(params, hash);
  }

  /** this function should only be called via the router, not directly */
  private setDisplayMode(
    displayMode: WhiteboardDisplayMode,
    focusedAnnotationId?: RecordId,
    animate = true
  ) {
    this.state.displayMode = {
      mode: displayMode,
      previousMode: this.state.displayMode.mode,
      focusedAnnotationId,
      animate,
    };
  }

  actionZoomIntoView() {
    this.whiteboardController.actionZoomIntoView();
  }

  actionOpenAhaDrawer({ url, useBottomDrawer = false }) {
    if (this.canOpenDrawer) {
      useBottomDrawer ? AhaBottomDrawer.showUrl(url) : AhaDrawer.showUrl(url);
    } else {
      // We are in a drawer, can't open within ourselves
      // We are in the desktop app which doesn't have a drawer. Just navigate.
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  actionOpenRecordInDrawer({
    recordRef,
    useBottomDrawer = false,
  }: {
    recordRef: RecordReferenceStruct;
    useBottomDrawer?: boolean;
  }) {
    // Open in new window if we're already in a drawer
    if (!this.canOpenDrawer) {
      const path = pathForRecordRef(recordRef);

      if (path) {
        window.open(path, '_blank', 'noopener,noreferrer');
      }

      return;
    }

    const Drawer = useBottomDrawer ? AhaBottomDrawer : AhaDrawer;

    const drawer = Drawer.instance();
    if (!drawer) return;

    drawer.openRecord(recordRef);
  }

  actionCloseAhaDrawer = () => {
    // Don't close the drawer if we were opened from the drawer, otherwise we are closing our own container
    if (this.whiteboardIsInDrawer) {
      return;
    }

    // Close the whiteboard drawer if we're in full screen mode
    AhaDrawer.hide();
    AhaBottomDrawer.hide();
  };

  get whiteboardIsInDrawer(): boolean {
    return !!this.inDocNode?.closest('.drawer.in');
  }

  get canOpenDrawer(): boolean {
    return !this.whiteboardIsInDrawer && AhaDrawer.canOpen();
  }

  async lookupUserInfo(userId) {
    if (!this.collaborative) {
      return { name: 'Anonymous', avatar: null };
    }
    try {
      const { sharingOptions } = this.state;
      const response = await fetch(
        `${sharingOptions.collaborationBaseURL}/api/v1/whiteboards/${this.state.whiteboardId}/users/${userId}.json?fields=name,avatar_url`,
        { headers: this.authorizationHeaders }
      ).then(
        response =>
          response.json() as Promise<{
            user: { name: string; avatar_url: string };
          }>
      );

      return { name: response.user.name, avatar: response.user.avatar_url };
    } catch {
      return { name: 'Anonymous', avatar: null };
    }
  }

  // Resolves attachment IDs to a URL. For shared whiteboards, we have
  // to hit the server to fetch the external URL, because we can't use
  // a shared URL as the src, because we can't pass along our
  // credentials. To avoid slamming the server with requests, we batch
  // them up every few hundred milliseconds, and do one bulk request.
  resolveAttachmentInfo = (() => {
    let promises = new Map();
    let timer = null as ReturnType<typeof setTimeout> | null;

    return attachmentId => {
      if (this.isShared) {
        if (!timer) {
          timer = setTimeout(() => {
            const activePromises = promises;
            promises = new Map();
            timer = null;

            const body = new FormData();

            for (const key of activePromises.keys()) {
              body.append('attachment_ids[]', key);
            }

            void fetch(
              `${this.state.sharingOptions.collaborationBaseURL}/api/v1/whiteboards/${this.state.whiteboardId}/show_attachments`,
              {
                method: 'POST',
                headers: this.authorizationHeaders,
                body,
              }
            )
              .then(
                response =>
                  response.json() as Promise<{
                    attachments: Array<AttachmentType>;
                  }>
              )
              .then(data => {
                data.attachments.forEach(attachment => {
                  (activePromises.get(attachment.id) || []).forEach(
                    ({ resolve, reject }) => {
                      resolve({
                        url: attachment.download_url,
                      });
                    }
                  );

                  activePromises.delete(attachment.id);
                });

                for (const value of activePromises.values()) {
                  value.forEach(({ resolve, reject }) => {
                    // Failing to load an attachment is not something we want to
                    // take action on. You can easily create a broken attachment
                    // by copying an image from one whiteboard and pasting it
                    // into a whiteboard in a different account. That will cause
                    // an image load failure.
                    //
                    // We show the user a blank image, so they have the feedback
                    // they need that something went wrong.
                    console.error('Failed to fetch attachment', value);
                    resolve();
                  });
                }
              });
          }, 300);
        }

        const promise: Promise<{ url: string }> = new Promise(
          (resolve, reject) => {
            promises.set(
              attachmentId,
              (promises.get(attachmentId) || []).concat([{ resolve, reject }])
            );
          }
        );

        return promise;
      } else {
        return Promise.resolve({ url: `/attachments/${attachmentId}` });
      }
    };
  })();

  async uploadAttachment({ file }) {
    const { sharingOptions } = this.state;
    const formData = new FormData();

    formData.append('attachment[data]', file);
    const response = await fetch(
      `${sharingOptions.collaborationBaseURL}/api/v1/whiteboards/${this.state.whiteboardId}/attachments`,
      {
        method: 'POST',
        headers: {
          'X-CSRF-Token': sharingOptions.csrfToken || window.csrfToken(),
          ...this.authorizationHeaders,
        },
        body: formData,
      }
    );
    const json = (await response.json()) as { attachment: AttachmentType };
    return json.attachment.id;
  }

  async duplicateAttachments(attachmentMapping, originalWhiteboardId) {
    const { sharingOptions } = this.state;
    const formData = new FormData();

    formData.append('original_whiteboard_id', originalWhiteboardId);
    formData.append('attachment_mapping', JSON.stringify(attachmentMapping));

    const response = await fetch(
      `${sharingOptions.collaborationBaseURL}/api/v1/whiteboards/${this.state.whiteboardId}/duplicate_attachments`,
      {
        method: 'POST',
        headers: {
          'X-CSRF-Token': sharingOptions.csrfToken || window.csrfToken(),
          ...this.authorizationHeaders,
        },
        body: formData,
      }
    );

    return response.json() as Promise<{ [objId: RecordId]: RecordId }>;
  }

  async actionSelectAhaRecords(
    options?: RecordPickerOptions
  ): Promise<{ typename: string; id: RecordId; path: string }[]> {
    return new Promise<{ typename: string; id: RecordId; path: string }[]>(
      resolve => {
        sharedRecordPickerController.actionOpenSearchModal({
          actionLabel: 'Add records',
          // @ts-expect-error: KeyResult is not yet supported, but will just be filtered out by the search interface
          searchableTypes: WHITEBOARD_RECORD_TYPES.slice(),
          onSelect(searchDocuments) {
            const records = searchDocuments
              .map(searchDocument => searchDocument.searchable)
              .filter(s => s?.path && s?.typename && s?.id) as {
              typename: string;
              id: RecordId;
              path: string;
            }[];

            resolve(records);
          },
          ...options,
        });
      }
    );
  }

  actionSetCommentsVisible(visible: boolean) {
    this.state.commentsVisible = visible;
  }

  actionEditLockedDocument() {
    this.state.editingLockedDocument = true;
  }

  loadDefaultCardLayout: WhiteboardLoadDefaultCardLayoutFunction = async () => {
    const cardLayoutScope = CardLayout.buildCardLayoutScope({
      cardViews: ['whiteboard'],
    });

    if (!this.isShared) {
      const whiteboard = await Whiteboard.merge({
        defaultCardLayout: cardLayoutScope,
      }).find(this.props.whiteboardId);

      // @ts-expect-error Internal model type is incompatiable with the Aha.CardLayout type
      return whiteboard?.defaultCardLayout as Aha.CardLayout | null;
    }

    return null;
  };

  actionOpenCardLayoutEditor: WhiteboardCardLayoutEditorFunction = ({
    callback,
    visibleRecordTypes,
    currentRecordType,
    cardLayout,
  }) => {
    return new Promise(resolve => {
      void sharedCardLayoutController.actionShowWhiteboardCardLayoutEditor({
        // @ts-expect-error The CardLayout vs. Aha.CardLayout types aren't identical
        callback,
        // @ts-expect-error The CardLayout vs. Aha.CardLayout types aren't identical
        cardLayout: cardLayout as CardLayout,
        currentRecordType,
        visibleRecordTypes,
        whiteboardId: this.props.whiteboardId,
      });
    });
  };

  // File name is requested by querying the Whiteboard model through GraphQL.
  // For whiteboards shared with another account or personal whiteboards shared
  // with the same account, this will throw an authorization error. In those
  // cases we should not make the request and use the default file name.
  canRequestFilename() {
    const shared =
      this.state.sharingOptions.collaborationBaseURL !== window.location.origin;
    const otherAccount = this.fromOtherAccount();
    // Whiteboard is not shared - can request doc title
    if (!shared) return true;

    // Whiteboard is shared from another account - can't request doc title
    if (otherAccount) return false;

    // Whiteboard is shared from this account - request doc title if we have
    // the page in state and it's NOT a personal whiteboard
    return !!(
      this.state.whiteboard?.page && !this.state.whiteboard?.page?.isPersonal
    );
  }

  getPageId = (): RecordId | undefined => {
    return this.state.whiteboard?.page?.id;
  };

  // For embedded whiteboard references to a standalone whiteboard,
  // returns the URL to the standalone whiteboard.
  originalWhiteboardUrl(): string | undefined {
    const { whiteboard } = this.state;
    const { containingPageId } = this.props;
    if (
      containingPageId &&
      whiteboard?.page?.id &&
      containingPageId !== whiteboard.page.id
    ) {
      return whiteboard.page.path;
    }
  }
}

async function renderDiagramToProjectRecords(diagram: Diagram) {
  const measureTextFunc: MeasureText = async (text, options) => {
    return { width: 200, height: 75, fontSize: 14 };
  };
  await diagram.fullLayout({ measureTextFunc });

  const json = diagram.renderToWhiteboard({
    // @ts-expect-error recordId is expected to be a number string, but can actually be any kind of string
    nodeAttrs: (node: DiagramNode) => ({
      shapeName: 'record',
      recordType: 'projects',
      recordId: node.title,
    }),
    edgeAttrs: () => ({
      endStyles: [0, 10],
      strokeWidth: 2,
      stroke: '#5CA5E0',
    }),
  });

  return json;
}

export default WhiteboardContainerController;
